import { useEffect, useRef } from "react";
// Platform-stack image is served locally from /public/platform as optimized
// AVIF/WebP (~60-68 KB) instead of the original ~1.9 MB Lovable CDN PNG (which
// also 404s off mane.dev). See the <picture> below.

const ITEMS = [
  {
    title: "Managed Solution",
    body: "AI transformation tailored to how your enterprise actually operates.",
  },
  {
    title: "Agent Orchestrator",
    body: "Coordinated execution with approvals, guardrails, observability, and human control.",
  },
  {
    title: "Knowledge Fabric",
    body: "Fragmented enterprise context transformed into AI-ready memory and reusable workflows.",
  },
  {
    title: "Data Connectivity",
    body: "Unified interoperability across your systems, applications, models, and data environments.",
  },
  {
    title: "Building Blocks",
    body: "Reusable agents, connectors, tools, and workflows for mission-critical AI.",
  },
];

export default function ManePlatform() {
  const cardsRef = useRef<HTMLUListElement>(null);

  useEffect(() => {
    const el = cardsRef.current;
    if (!el) return;
    if (window.matchMedia("(min-width: 768px)").matches) return;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;

    // Only run carousel if cards are in horizontal-scroll layout
    if (el.scrollWidth <= el.clientWidth) return;

    // Start centered on the middle card
    const centerOn = (index: number, behavior: ScrollBehavior = "auto") => {
      const child = el.children[index] as HTMLElement | undefined;
      if (!child) return;
      const target = child.offsetLeft - (el.clientWidth - child.clientWidth) / 2;
      el.scrollTo({ left: target, behavior });
    };

    const middle = Math.floor(ITEMS.length / 2);
    centerOn(middle, "auto");

    let current = middle;
    let paused = false;
    const pause = () => {
      paused = true;
      window.clearTimeout(resumeTimer);
      resumeTimer = window.setTimeout(() => (paused = false), 3500);
    };
    let resumeTimer = 0;
    el.addEventListener("pointerdown", pause, { passive: true });
    el.addEventListener("wheel", pause, { passive: true });
    el.addEventListener("touchstart", pause, { passive: true });

    const interval = window.setInterval(() => {
      if (paused) return;
      current = (current + 1) % ITEMS.length;
      centerOn(current, "smooth");
    }, 2800);

    return () => {
      window.clearInterval(interval);
      window.clearTimeout(resumeTimer);
      el.removeEventListener("pointerdown", pause);
      el.removeEventListener("wheel", pause);
      el.removeEventListener("touchstart", pause);
    };
  }, []);

  return (
    <section
      aria-labelledby="mane-platform-heading"
      className="mane-platform"
    >
      <div className="mane-platform__inner">
        <div className="mane-platform__head">
          <p className="eyebrow mane-platform__eyebrow">The Mane platform</p>
          <h2 id="mane-platform-heading" className="h-display mane-platform__title">
            Agents that <em>actually</em> ship.
          </h2>
          <p className="lead mane-platform__sub">
            One platform to run, govern, and scale every agent you build. On the systems you already trust.
          </p>
        </div>

        <div className="mane-platform__artboard">
          <picture>
            {/* Mobile portrait, then desktop — AVIF first, WebP fallback. */}
            <source media="(max-width: 767px)" srcSet="/platform/mane-platform-stack-mobile.avif" type="image/avif" />
            <source media="(max-width: 767px)" srcSet="/platform/mane-platform-stack-mobile.webp" type="image/webp" />
            <source srcSet="/platform/mane-platform-stack.avif" type="image/avif" />
            <source srcSet="/platform/mane-platform-stack.webp" type="image/webp" />
            <img
              src="/platform/mane-platform-stack.webp"
              alt=""
              aria-hidden="true"
              className="mane-platform__img"
              loading="lazy"
              decoding="async"
            />
          </picture>

          {/* Desktop / tablet overlay */}
          <div className="mane-platform__overlay" aria-hidden="true">
            {ITEMS.map((it) => (
              <div
                key={it.title}
                className="mane-platform__item"
              >
                <h3 className="mane-platform__item-title">{it.title}</h3>
                <p className="mane-platform__item-body">{it.body}</p>
              </div>
            ))}
          </div>

          {/* Semantic list for SR + mobile rendering */}
          <ul ref={cardsRef} className="mane-platform__cards" role="list">
            {ITEMS.map((it) => (
              <li key={it.title} className="mane-platform__card">
                <h3 className="mane-platform__card-title">{it.title}</h3>
                <p className="mane-platform__card-body">{it.body}</p>
              </li>
            ))}
          </ul>
        </div>
      </div>

      <style>{`
        .mane-platform { width: 100%; padding: clamp(56px, 7vw, 96px) clamp(18px, 3vw, 32px); background: #fbf8f2; color: #151515; }
        :root.dark .mane-platform { background: #0a0a0a; color: #f5f5f4; }
        .mane-platform__inner { max-width: 1440px; margin: 0 auto; }
        .mane-platform__head {
          max-width: 980px; margin: 0 auto clamp(28px, 4vw, 56px); text-align: center;
        }
        .mane-platform__eyebrow { margin: 0 0 14px; }
        .mane-platform__title { color: #111318; margin: 0; }
        :root.dark .mane-platform__title { color: #f5f5f4; }
        .mane-platform__sub {
          margin: 18px auto 0;
          max-width: 680px;
          color: rgba(17,19,24,0.66);
          font-size: clamp(14px, 1.1vw, 17px);
          line-height: 1.45;
          letter-spacing: -0.01em;
        }
        :root.dark .mane-platform__sub { color: rgba(245,245,244,0.66); }

        .mane-platform__artboard {
          position: relative;
          width: 92%;
          max-width: 1200px;
          margin: 0 auto;
          aspect-ratio: 3 / 2;
          border-radius: clamp(20px, 2.2vw, 32px);
          overflow: hidden;
          box-shadow: 0 20px 60px rgba(86, 61, 34, 0.10);
          background: #fbf8f2;
        }
        :root.dark .mane-platform__artboard {
          background: #0a0a0a;
          box-shadow: 0 20px 60px rgba(0,0,0,0.5), inset 0 0 0 1px rgba(255,255,255,0.06);
        }
        :root.dark .mane-platform__artboard::after {
          content: "";
          position: absolute;
          inset: 0;
          z-index: 1;
          pointer-events: none;
          background: linear-gradient(90deg, rgba(10,10,10,0) 0%, rgba(10,10,10,0) 62%, rgba(10,10,10,0.28) 75%, rgba(10,10,10,0.78) 100%);
        }
        @media (max-width: 767px) {
          :root.dark .mane-platform__artboard::after { display: none; }
        }
        .mane-platform__img {
          position: absolute; inset: 0;
          width: 100%; height: 100%;
          object-fit: cover; object-position: center center;
          display: block;
        }
        :root.dark .mane-platform__img {
          filter: invert(0.92) hue-rotate(180deg) brightness(0.95) contrast(0.95);
        }
        .mane-platform__overlay {
          position: absolute;
          inset: 0;
          left: 55%;
          z-index: 2;
          pointer-events: none;
          display: flex;
          flex-direction: column;
          justify-content: center;
          align-items: flex-start;
          gap: clamp(8px, 1.2vw, 14px);
          padding: clamp(16px, 2vw, 28px) clamp(12px, 2vw, 24px);
        }
        .mane-platform__item {
          width: 82%;
          max-width: 380px;
          padding: 10px 14px;
          background: rgba(255,255,255,0.88);
          -webkit-backdrop-filter: none;
          backdrop-filter: none;
          border: 1px solid rgba(255,255,255,0.35);
          border-radius: 14px;
          box-shadow: 0 4px 20px rgba(0,0,0,0.1), 0 0 0 1px rgba(0,0,0,0.03);
        }
        :root.dark .mane-platform__item {
          background: rgba(15,15,15,0.45);
          border: 1px solid rgba(255,255,255,0.1);
          box-shadow: 0 4px 20px rgba(0,0,0,0.35), 0 0 0 1px rgba(255,255,255,0.04);
        }
        .mane-platform__item-title {
          font-size: clamp(14px, 1.35vw, 22px);
          font-weight: 700;
          letter-spacing: -0.035em;
          line-height: 1.1;
          color: #151515;
          margin: 0 0 4px;
        }
        :root.dark .mane-platform__item-title { color: #f5f5f4; }
        .mane-platform__item-body {
          font-size: clamp(11px, 0.85vw, 14px);
          font-weight: 430;
          letter-spacing: -0.012em;
          line-height: 1.4;
          color: rgba(21,21,21,0.78);
          max-width: 360px;
          margin: 0;
        }
        :root.dark .mane-platform__item-body { color: rgba(245,245,244,0.8); }

        .mane-platform__cards { display: none; list-style: none; padding: 0; margin: 0; }

        @media (min-width: 768px) and (max-width: 1023px) {
          .mane-platform__overlay { left: 54%; padding: 14px 10px; }
          .mane-platform__item { width: 88%; padding: 10px 12px; }
          .mane-platform__item-title { font-size: clamp(13px, 1.6vw, 17px); margin-bottom: 3px; }
          .mane-platform__item-body { font-size: clamp(10px, 1.15vw, 12px); line-height: 1.35; }
        }

        @media (max-width: 767px) {
          .mane-platform { padding: 56px 18px; }
          .mane-platform__title { font-size: clamp(38px, 11vw, 56px); line-height: 0.98; }
          .mane-platform__sub { font-size: 17px; max-width: 95%; }
          .mane-platform__inner { position: relative; }
          .mane-platform__artboard {
            aspect-ratio: auto;
            max-height: none;
            min-height: 0;
            height: auto;
            border-radius: 20px;
            overflow: visible;
          }
          .mane-platform__img {
            position: relative;
            inset: auto;
            width: 100%;
            height: auto;
            object-fit: contain;
            object-position: center top;
            border-radius: 16px;
          }
          .mane-platform__overlay { display: none; }
          .mane-platform__cards {
            display: flex;
            flex-direction: column;
            gap: 10px;
            margin: 0;
            padding: 14px 18px;
            position: absolute;
            inset: 0;
            z-index: 3;
            justify-content: center;
            align-items: center;
            transform: translateY(2%);
            pointer-events: none;
          }
          .mane-platform__card {
            background: rgba(255,255,255,0.9);
            -webkit-backdrop-filter: none;
            backdrop-filter: none;
            border: 1px solid rgba(255,255,255,0.35);
            border-radius: 14px;
            padding: 14px 16px;
            box-shadow: 0 4px 20px rgba(0,0,0,0.1), 0 0 0 1px rgba(0,0,0,0.03);
            pointer-events: auto;
            width: 100%;
            max-width: 420px;
          }
          .mane-platform__card-title {
            font-size: 15.5px;
            font-weight: 700;
            line-height: 1.15;
            letter-spacing: -0.025em;
            color: #111;
            margin: 0;
          }
          .mane-platform__card-body {
            margin: 5px 0 0;
            font-size: 13.5px;
            line-height: 1.4;
            letter-spacing: -0.01em;
            color: rgba(21,21,21,0.78);
          }
          :root.dark .mane-platform__card {
            background: rgba(15,15,15,0.45);
            border: 1px solid rgba(255,255,255,0.1);
            box-shadow: 0 4px 20px rgba(0,0,0,0.35), 0 0 0 1px rgba(255,255,255,0.04);
          }
          :root.dark .mane-platform__card-title { color: #f5f5f4; }
          :root.dark .mane-platform__card-body { color: rgba(245,245,244,0.8); }
        }

        @media (prefers-reduced-motion: reduce) {
          .mane-platform * { animation: none !important; transition: none !important; }
        }
      `}</style>
    </section>
  );
}
